import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import DynamicTable from "../../../data/DynamicTable/dynamicTable";
import { companyManagementUrl, salesManagement } from "../../../config";
import { useEffect, useState } from "react";
import { PulseLoader } from "react-spinners";
import { CompanyColumns } from "../../../data/DynamicTable/CompanyManagementColumns";
import {
  allCompanies,
  AllPurchaseOrders,
  AllPurchaseOrdersReverseView,
  AllPurchaseOrdersView,
} from "../../../data/Axios/queries";
import { graphqlQuery } from "../../../data/Axios/DynamicService";
import { tokens } from "../../../theme";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { format } from "date-fns";
import PurchaseOrderForm from "../PurchaseOrder/form";
import PaymentVoucherForm from "../PaymentVoucher/form";

const base_url = salesManagement.uri;

const actions = {
  entity: "PurchaseOrder",
  isSubRight: true,
  ViewPurchaseOrderItems: {
    key: "MARK_TASK_DONE",
    button_name: "View Order Items",
    Show_Button: true,
    permission: "hasNoPermission",
    roleGroupId: [1, 2, 3, 4],
  },
  showMore: {
    key: "SHOWMORE",
    button_name: "Show More",
    Show_Button: true,
    permission: "EditPermission",
    title: "Purchase Order Details",
    redirect: "/payment-voucher",
  },

  showStatus: true,
  showDelivery: true,
  showPayments: true,
  restrict: {
    restrict: true,
    field: "productCode",
    value: "",
  }
};

function ReversePurchaseOrders() {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [branchFilter, setBranchFilter] = useState("all");
  const [branches, setBranches] = useState([]);

  // Statistics
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalAmount: 0,
    paidOrders: 0,
    pendingOrders: 0,
    deliveredOrders: 0,
    pendingDeliveries: 0,
    outstandingBalance: 0,
    averageOrderValue: 0,
    averageOutstanding: 0,
  });

  // MUI theme-aware colors
  const muiTheme = useTheme();
  const colors = tokens(muiTheme.palette.mode);
  const isDark = muiTheme.palette.mode === "dark";

  const theme = {
    primary: isDark ? colors.blueAccent[400] : "#1976D2",
    secondary: isDark ? colors.blueAccent[500] : "#0288D1",
    success: "#4CAF50",
    warning: "#FF9800",
    error: "#F44336",
    lightBg: isDark ? alpha(colors.blueAccent[400], 0.15) : "#E3F2FD",
    cardBg: isDark ? colors.primary[400] : "#FFFFFF",
    pageBg: isDark ? colors.primary[500] : "#F5F7FA",
    iconBadgeGreen: isDark ? alpha("#4CAF50", 0.15) : "#E8F5E9",
    iconBadgeOrange: isDark ? alpha("#FF9800", 0.15) : "#FFF3E0",
    iconBadgeRed: isDark ? alpha("#F44336", 0.15) : "#FFEBEE",
  };

  const ignore = [
    "deliveryStatus",
    "paymentStatus",
    "expiryDate",
    "createdBy",
    "updatedBy",
    "productCode",
    "vendorId",
    "companyId",
    "createdDate",
    "createdBy",
    "updatedDate",
    "companyName",
    "addedBy",
    "modifiedBy",
  ];
  const ignoreshowMore = [
    "createdBy",
    "updatedBy",
    "vendorId",
    "companyId"]

  // ✅ Calculate statistics
  const calculateStats = (data) => {
    const totalOrders = data.length;
    const totalAmount = data.reduce((sum, order) => sum + (order.totalAmount || 0), 0);
    const outstandingBalance = data.reduce(
      (sum, order) => sum + (order.outstandingBalance || 0),
      0
    );

    const paidOrders = data.filter(
      (order) => order.paymentStatus === 1
    ).length;
    const pendingOrders = data.filter(
      (order) => order.paymentStatus === 0
    ).length;

    const deliveredOrders = data.filter(
      (order) => order.deliveryStatus === 1
    ).length;
    const pendingDeliveries = data.filter(
      (order) => order.deliveryStatus === 0
    ).length;

    // Average order value should be based on total amount, not outstanding balance
    const averageOrderValue = totalOrders > 0 ? totalAmount / totalOrders : 0;

    // Average outstanding balance (only for orders with outstanding balance > 0)
    const ordersWithBalance = data.filter(order => (order.outstandingBalance || 0) > 0);
    const averageOutstanding = ordersWithBalance.length > 0
      ? outstandingBalance / ordersWithBalance.length
      : 0;

    setStats({
      totalOrders,
      totalAmount,
      paidOrders,
      pendingOrders,
      deliveredOrders,
      pendingDeliveries,
      outstandingBalance,
      averageOrderValue,
      averageOutstanding,
    });
  };

  // ✅ Extract unique branches
  const extractBranches = (data) => {
    const uniqueBranches = [...new Set(
      data
        .map(order => order.branchName)
        .filter(branch => branch != null && branch !== "")
    )].sort();

    setBranches(uniqueBranches);
  };

  // ✅ Fetch purchase orders
  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await graphqlQuery(AllPurchaseOrdersReverseView, base_url);

      if (Array.isArray(response)) {
        const ordersData = response.map((order) => ({
          ...order,
          formattedDate: order.createdDate
            ? format(new Date(order.createdDate), "yyyy-MM-dd")
            : "",
          formattedOrderDate: order.orderDate
            ? format(new Date(order.orderDate), "yyyy-MM-dd")
            : "",
        }));

        setPurchaseOrders(ordersData);
        extractBranches(ordersData);
        applyFilters(
          ordersData,
          searchQuery,
          paymentStatusFilter,
          deliveryStatusFilter,
          dateFilter,
          activeFilter,
          branchFilter
        );
      }
    } catch (error) {
      console.error("Error fetching purchase orders:", error);
      setError("Failed to load purchase orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Apply all filters
  const applyFilters = (data, search, payment, delivery, date, active, branch) => {
    let filtered = [...data];

    // Search filter - Fixed to match actual field names from GraphQL
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter((order) => {
        const fields = [
          order.purchaseOrder,
          order.vendorName,
          order.companyName,
          order.productCode,
          order.vendorComments,
          order.addedBy,
          order.modifiedBy,
        ];

        return fields.some((field) => {
          if (field == null) return false;
          return String(field).toLowerCase().includes(searchLower);
        });
      });
    }

    // Payment status filter
    if (payment !== "all") {
      const paymentValue = parseInt(payment);
      filtered = filtered.filter((order) => order.paymentStatus === paymentValue);
    }

    // Delivery status filter
    if (delivery !== "all") {
      const deliveryValue = parseInt(delivery);
      filtered = filtered.filter((order) => order.deliveryStatus === deliveryValue);
    }

    // Active filter
    if (active !== "all") {
      const activeValue = parseInt(active);
      filtered = filtered.filter((order) => order.isActive === activeValue);
    }

    // Branch filter
    if (branch !== "all") {
      filtered = filtered.filter((order) => order.branchName === branch);
    }

    // Date filter
    if (date !== "all") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (date === "today") {
        filtered = filtered.filter((order) => {
          const orderDate = new Date(order.createdDate);
          orderDate.setHours(0, 0, 0, 0);
          return orderDate.getTime() === today.getTime();
        });
      } else if (date === "week") {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        filtered = filtered.filter((order) => {
          const orderDate = new Date(order.createdDate);
          return orderDate >= weekAgo;
        });
      } else if (date === "month") {
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        filtered = filtered.filter((order) => {
          const orderDate = new Date(order.createdDate);
          return orderDate >= monthAgo;
        });
      }
    }

    setFilteredOrders(filtered);
    calculateStats(filtered);

    // Calculate total amount
    const total = filtered.reduce((sum, order) => sum + (order.totalAmount || 0), 0);
    const formattedTotal = total.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    setTotalAmount(formattedTotal);
  };

  // ✅ Handle filter changes
  useEffect(() => {
    if (purchaseOrders.length > 0) {
      applyFilters(
        purchaseOrders,
        searchQuery,
        paymentStatusFilter,
        deliveryStatusFilter,
        dateFilter,
        activeFilter,
        branchFilter
      );
    }
  }, [searchQuery, paymentStatusFilter, deliveryStatusFilter, dateFilter, activeFilter, branchFilter]);

  // ✅ Handle refresh
  const handleRefresh = () => {
    fetchPurchaseOrders();
  };

  // ✅ Clear all filters
  const handleClearFilters = () => {
    setSearchQuery("");
    setPaymentStatusFilter("all");
    setDeliveryStatusFilter("all");
    setDateFilter("all");
    setActiveFilter("all");
    setBranchFilter("all");
  };

  // ✅ Check if filters are active
  const hasActiveFilters =
    searchQuery ||
    paymentStatusFilter !== "all" ||
    deliveryStatusFilter !== "all" ||
    dateFilter !== "all" ||
    activeFilter !== "all" ||
    branchFilter !== "all";

  // ✅ Fetch data on component mount
  useEffect(() => {
    fetchPurchaseOrders();
  }, []);

  return (
    <Box sx={{ p: 3, bgcolor: theme.pageBg, minHeight: "100vh" }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <ShoppingCartIcon sx={{ fontSize: 32, color: theme.primary, mr: 1 }} />
          <Typography variant="h4" fontWeight="700" color={theme.primary}>
            Purchase Order Management
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Track and manage all purchase orders and vendor transactions
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Total Amount Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              background: isDark
                ? `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`
                : `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
              color: "white",
              border: isDark ? `1px solid ${alpha(colors.blueAccent[400], 0.4)}` : "none",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                width: "150px",
                height: "150px",
                background: "rgba(255, 255, 255, 0.1)",
                borderRadius: "50%",
                top: "-50px",
                right: "-50px",
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <AttachMoneyIcon sx={{ fontSize: 28, mr: 1 }} />
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Total Amount
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="700">
                KSh {totalAmount}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, mt: 1 }}>
                {stats.totalOrders} orders • Avg: KSh {stats.averageOrderValue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Status Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                  >
                    Payment Status
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                    <Box>
                      <Typography variant="h5" fontWeight="700" color={theme.success}>
                        {stats.paidOrders}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Paid
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Typography variant="h5" fontWeight="700" color={theme.warning}>
                        {stats.pendingOrders}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pending
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ bgcolor: theme.iconBadgeGreen, p: 1.5, borderRadius: 2 }}>
                  <AccountBalanceIcon sx={{ color: theme.success, fontSize: 28 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Delivery Status Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                  >
                    Delivery Status
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                    <Box>
                      <Typography variant="h5" fontWeight="700" color={theme.success}>
                        {stats.deliveredOrders}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Delivered
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Typography variant="h5" fontWeight="700" color={theme.warning}>
                        {stats.pendingDeliveries}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pending
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ bgcolor: theme.iconBadgeOrange, p: 1.5, borderRadius: 2 }}>
                  <LocalShippingIcon sx={{ color: theme.warning, fontSize: 28 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Outstanding Balance Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                  >
                    Outstanding Balance
                  </Typography>
                  <Typography variant="h4" fontWeight="700" color={theme.error}>
                    KSh{" "}
                    {stats.outstandingBalance.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg: KSh {stats.averageOutstanding.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: theme.iconBadgeRed, p: 1.5, borderRadius: 2 }}>
                  <PendingActionsIcon sx={{ color: theme.error, fontSize: 28 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter and Actions Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          bgcolor: theme.cardBg,
        }}
      >
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <FilterListIcon sx={{ color: theme.primary }} />
          <Typography variant="h6" fontWeight="600">
            Filters
          </Typography>
          {hasActiveFilters && (
            <Chip
              label={`${[
                searchQuery,
                paymentStatusFilter !== "all",
                deliveryStatusFilter !== "all",
                dateFilter !== "all",
                activeFilter !== "all",
                branchFilter !== "all",
              ].filter(Boolean).length
                } Active`}
              size="small"
              color="primary"
              onDelete={handleClearFilters}
            />
          )}
        </Box>

        <Grid container spacing={2} alignItems="center">
          {/* Search */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by PO#, vendor, company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Branch Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Branch</InputLabel>
              <Select
                value={branchFilter}
                label="Branch"
                onChange={(e) => setBranchFilter(e.target.value)}
              >
                <MenuItem value="all">All Branches</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch} value={branch}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <StorefrontIcon sx={{ fontSize: 18, color: theme.primary }} />
                      {branch}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Payment Status Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={paymentStatusFilter}
                label="Payment Status"
                onChange={(e) => setPaymentStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="1">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: theme.success }} />
                    Paid
                  </Box>
                </MenuItem>
                <MenuItem value="0">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PendingActionsIcon sx={{ fontSize: 18, color: theme.warning }} />
                    Unpaid/Pending
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Delivery Status Filter */}
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Delivery Status</InputLabel>
              <Select
                value={deliveryStatusFilter}
                label="Delivery Status"
                onChange={(e) => setDeliveryStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="1">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: theme.success }} />
                    Delivered
                  </Box>
                </MenuItem>
                <MenuItem value="0">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocalShippingIcon sx={{ fontSize: 18, color: theme.warning }} />
                    Pending
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Date Filter */}
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateFilter}
                label="Date Range"
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Active Filter */}
          <Grid item xs={12} sm={6} md={1}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={activeFilter}
                label="Status"
                onChange={(e) => setActiveFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="1">Active</MenuItem>
                <MenuItem value="0">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Actions */}
          <Grid item xs={12} md={1}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{
                    bgcolor: theme.lightBg,
                    "&:hover": { bgcolor: theme.primary, color: "white" },
                  }}
                >
                  {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {searchQuery && (
              <Chip
                label={`Search: "${searchQuery}"`}
                size="small"
                onDelete={() => setSearchQuery("")}
                color="primary"
                variant="outlined"
              />
            )}
            {branchFilter !== "all" && (
              <Chip
                label={`Branch: ${branchFilter}`}
                size="small"
                onDelete={() => setBranchFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
            {paymentStatusFilter !== "all" && (
              <Chip
                label={`Payment: ${paymentStatusFilter === "1" ? "Paid" : "Unpaid/Pending"}`}
                size="small"
                onDelete={() => setPaymentStatusFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
            {deliveryStatusFilter !== "all" && (
              <Chip
                label={`Delivery: ${deliveryStatusFilter === "1" ? "Delivered" : "Pending"}`}
                size="small"
                onDelete={() => setDeliveryStatusFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
            {dateFilter !== "all" && (
              <Chip
                label={`Date: ${dateFilter.charAt(0).toUpperCase() + dateFilter.slice(1)
                  }`}
                size="small"
                onDelete={() => setDateFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
            {activeFilter !== "all" && (
              <Chip
                label={`Status: ${activeFilter === "1" ? "Active" : "Inactive"}`}
                size="small"
                onDelete={() => setActiveFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        )}
      </Paper>

      {/* Dynamic Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: theme.cardBg,
        }}
      >
        {loading && filteredOrders.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DynamicTable
            title="Purchase Orders"
            subtitle={`Showing ${filteredOrders.length} orders ${hasActiveFilters ? "(filtered)" : ""
              }`}
            ignoreFields={ignore}
            ignoreShowMore={ignoreshowMore}
            // FormComponent={PurchaseOrderForm}
            // FormComponent1={PaymentVoucherForm}
            query={AllPurchaseOrdersReverseView}
            base_url={base_url}
            actions={actions}
            filteredData={filteredOrders}
            isfilteredData={true}
          />
        )}
      </Paper>
    </Box>
  );
}

export default ReversePurchaseOrders;